import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiArrowLeft, FiArrowUpRight, FiDownload, FiMinus, FiPlus } from 'react-icons/fi';
import Navbar from '../Components/Navbar2';
import NewContactForm from '../Components/NewContactForm';
import './voices-contact.css';

const questions = [
  {
    title: 'Can we start with Salesforce or Agentforce?',
    answer: 'Yes. Start with the workflow you already have. The engagement can connect Salesforce, Agentforce, data, and integration work.',
  },
  {
    title: 'What happens after launch?',
    answer: 'We agree the support and improvement scope together. Managed-agent work can include evaluation, monitoring reviews, engineering changes, and clear human ownership.',
  },
];

const interests = ['Custom AI agents', 'Salesforce Implementation', 'Salesforce Agentforce', 'AI Implementation', 'Agentic Enterprise Implementation', 'OpenAI Implementation', 'Claude Implementation', 'Managed agents', 'Connected enterprise', 'Help me choose'];

export default function VoicesContact() {
  const { search } = useLocation();
  const [brief, setBrief] = useState(() => ({ name: '', email: '', company: '', message: '', focus: new URLSearchParams(search).get('focus')?.slice(0, 160) || interests[0] }));
  const [details, setDetails] = useState(false);
  const [projectBrief, setProjectBrief] = useState(null);
  const title = useRef(null);
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    title.current?.focus();
  }, [details]);
  const changeBrief = event => setBrief(previous => ({ ...previous, [event.target.name]: event.target.value }));
  function continueToDetails(event) {
    event.preventDefault();
    setProjectBrief({ name: brief.name.trim(), email: brief.email.trim(), company: brief.company.trim(), content: `Focus: ${brief.focus}\n\n${brief.message.trim()}` });
    setDetails(true);
  }
  function saveBrief() {
    const text = `ANTRAZAL / PROJECT BRIEF\n\nFocus: ${brief.focus}\nName: ${brief.name}\nEmail: ${brief.email}\nCompany: ${brief.company || 'Not specified'}\n\nWhat should work better:\n${brief.message}`;
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'antrazal-project-brief.txt';
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  return <>
    <Navbar />
    <main className="v-contact-page vc-contact" id="voices-main" tabIndex={-1} aria-labelledby="vc-contact-title" data-voices-theme="dark">
      <section className="vc-contact__layout">
        <div className="vc-contact__copy">
          <nav className="vc-contact__breadcrumb" aria-label="Breadcrumb"><Link to="/">Home</Link><span aria-hidden="true">/</span><span aria-current="page">Contact</span></nav>
          <p className="vc-contact__eyebrow">Let's talk</p>
          <h1 id="vc-contact-title">What should<br /><em>work better?</em></h1>
          <p className="vc-contact__intro">Tell us about the work. We'll start there.</p>
          <a className="vc-contact__email" href="mailto:info@antrazal.com">info@antrazal.com<FiArrowUpRight aria-hidden="true" /></a>
          <div className="vc-contact__note">
            <h2>Not sure where to start?</h2>
            <p>Share one workflow, the systems you use, and what you'd like to change.</p>
          </div>
        </div>
        <section className="vc-contact__form" aria-labelledby="vc-contact-form-title">
          <h2 id="vc-contact-form-title" ref={title} tabIndex={-1}>{details ? 'Contact details.' : 'Start a conversation.'}</h2>
          <div hidden={details}>
            <form className="vc-contact__brief" onSubmit={continueToDetails}>
              <div className="vc-contact__brief-grid">
                <label>Your name *<input name="name" required maxLength={120} autoComplete="name" placeholder="Full name" value={brief.name} onChange={changeBrief} /></label>
                <label>Work email *<input name="email" type="email" required maxLength={200} autoComplete="email" placeholder="you@company.com" value={brief.email} onChange={changeBrief} /></label>
                <label className="vc-contact__brief-full">Company (optional)<input name="company" maxLength={160} autoComplete="organization" placeholder="Your company" value={brief.company} onChange={changeBrief} /></label>
                <label className="vc-contact__brief-full">I'm interested in<select name="focus" value={brief.focus} onChange={changeBrief}>{(interests.includes(brief.focus) ? interests : [...interests, brief.focus]).map(interest => <option key={interest}>{interest}</option>)}</select></label>
                <label className="vc-contact__brief-full">What should work better? *<textarea name="message" required maxLength={2400} placeholder="What happens today? Which systems are involved? What should improve?" value={brief.message} onChange={changeBrief} /></label>
              </div>
              <div className="vc-contact__brief-actions"><button type="submit">Continue to contact details <FiArrowUpRight aria-hidden="true" /></button><button type="button" onClick={saveBrief}>Save brief <FiDownload aria-hidden="true" /></button></div>
            </form>
            <p className="vc-contact__reassurance">Share the workflow, not passwords, customer records, or confidential data. Agree the scope before a delivery commitment.</p>
            <p className="vc-contact__form-note">Nothing is submitted until you complete your contact details and choose Submit.</p>
          </div>
          {projectBrief && <div hidden={!details}><button className="vc-contact__back" type="button" onClick={() => setDetails(false)}><FiArrowLeft aria-hidden="true" /> Back to brief</button><NewContactForm projectBrief={projectBrief} /></div>}
        </section>
      </section>
      <section className="vc-contact__next" aria-labelledby="vc-contact-next-title">
        <p className="vc-contact__eyebrow">A clear next step</p>
        <h2 id="vc-contact-next-title">Start with the work.<br /><em>Decide on the fit.</em></h2>
        <div>
          <p>Share one workflow, the systems involved, and what a better result would look like.</p>
          <p>Use that brief to discuss scope, dependencies, human ownership, and delivery expectations before committing to a build.</p>
        </div>
      </section>
      <section className="vc-contact__faq" aria-label="Common questions">
        {questions.map(({ title, answer }) => <details key={title}>
          <summary>{title}<FiPlus className="vc-contact__expand" aria-hidden="true" /><FiMinus className="vc-contact__collapse" aria-hidden="true" /></summary>
          <p>{answer}</p>
        </details>)}
      </section>
    </main>
  </>;
}
